/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Globals Store - State Used Across the Entire Application
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { AlertSnackbar } from '@/types/typesGlobal'

export const useGlobalsStore = defineStore('globalsStore', () => {
  // //////////////////////////////////////////////////////////////////
  // 💬 System Globals
  // //////////////////////////////////////////////////////////////////
  const applicationName = ref('SnowFlow')
  const applicationDomain = ref(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://snowflow.lendio.com')
  const productName = ref('Snowflake')

  // const applicationDomainLocalEmulation = ref('http://localhost:3000')
  const docStore = useDocStore()
  const { getDoc } = docStore

  // //////////////////////////////////////////////////////////////////
  // 💬 Global Layouts
  // //////////////////////////////////////////////////////////////////
  const railCollapsedState = ref(true)

  // //////////////////////////////////////////////////////////////////
  // 💬 Account Profile Information
  // //////////////////////////////////////////////////////////////////

  // Note* Account and User Docs are Synced in the docStore.docs object
  const accountProfileDocSync = ref({} as any)
  const accountId = ref(null as string | null)
  const userId = ref(null as string | null)
  const accountNamespace = ref(null as string | null) // Default Snowflake Namespace

  const syncAccountProfile = (account: string) => {
    console.log(`syncAccountProfile: ${account}`)
    accountId.value = account

    getDoc('accountProfile', 'accounts', account, true, accountProfileDocSync)
  }

  const accountProfile = computed(() => {
    // 💬 Get Snowflake System Namespace
    // /////////////////////////////////
    console.log('Snowflake Settings', accountProfileDocSync.value?.snowflakeSettings)

    const systemDatabase = accountProfileDocSync.value?.snowflakeSettings?.systemOwnership?.database || ''
    const systemSchema = accountProfileDocSync.value?.snowflakeSettings?.systemOwnership?.schema || ''

    accountNamespace.value = systemDatabase ? `${systemDatabase}.${systemSchema}` : null

    return accountProfileDocSync.value
  })

  // //////////////////////////////////////////////////////////////////
  // User Profile Information
  // //////////////////////////////////////////////////////////////////
  const userProfileDocSync = ref({} as any)

  const syncUserProfile = (account: string, uid: string) => {
    console.log(`syncUserProfile: ${account} - ${uid}`)
    getDoc('userProfile', 'users', uid, true, userProfileDocSync)
  }

  const userProfile = computed(() => {
    const { firstName, lastName, snowflakeConnections, defaults } = userProfileDocSync.value
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : ''
    const initials = firstName && lastName ? `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}` : ''

    // Get Active Snowflake Connection from Defaults if Available
    let activeSnowflakeConnection = snowflakeConnections && defaults?.snowflakeConnection ? snowflakeConnections[defaults.snowflakeConnection] : null

    // If Not Using a Snowflake Connection, Set Active Snowflake Connection to Null
    if (activeSnowflakeConnection && activeSnowflakeConnection.connectionType === 'none') activeSnowflakeConnection = null

    return { ...userProfileDocSync.value, fullName, initials, activeSnowflakeConnection }
  })

  // //////////////////////////////////////////////////////////////////
  // Application Theme
  // //////////////////////////////////////////////////////////////////
  // "theme" has a cookie for remembering the user's theme preference
  const theme = ref('light' as 'light' | 'dark' | 'system') // app.vue will check for an active cookie
  const themeCookie = useCookie('theme')

  function toggleTheme() {
    theme.value = theme.value === 'dark' ? 'light' : 'dark'
    themeCookie.value = theme.value
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Throw Global Snackbar Alert
  // //////////////////////////////////////////////////////////////////
  const alertSnackbarState = ref<AlertSnackbar>({ visible: false, color: 'info', message: undefined, timeout: 3000 })

  const alertSnackbar = (message: string, color: 'info' | 'success' | 'warning' | 'error' = 'info', timeout: number = 3000) => {
    if (timeout === 0) timeout = -1 // -1 means the snackbar will not close automatically
    alertSnackbarState.value = { visible: true, color, message, timeout }
  }

  return {
    theme,
    applicationName,
    applicationDomain,
    railCollapsedState,
    accountId,
    userId,
    accountProfile,
    accountNamespace,
    userProfile,
    alertSnackbarState,
    alertSnackbar,
    toggleTheme,
    syncAccountProfile,
    syncUserProfile,
  }
})
